import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, ChevronLeft, X, Upload, AlertCircle, CheckCircle, FileText, Loader, Download, HelpCircle } from 'lucide-react';
import fileUploadService from '../../services/fileUploadService';
import crypto from 'crypto-js';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const FileUpload = () => {
    const [files, setFiles] = useState([]);
    const [processedData, setProcessedData] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [showProcessedData, setShowProcessedData] = useState(false);
    const [confirmationData, setConfirmationData] = useState(null);
    const [showHowToUse, setShowHowToUse] = useState(false);
    const navigate = useNavigate();
    const { studyID } = useParams();

    const isValidCSV = (file) => {
        return file.type === 'text/csv' || file.name.endsWith('.csv');
    };

    const processCSV = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                const lines = text.split('\n').filter(line => line.trim() !== '');
                const headers = lines[0].split(',').map(header => header.trim());
                const data = [];

                for (let i = 1; i < lines.length; i++) {
                    const values = lines[i].split(',').map(value => value.trim());
                    if (values.length === headers.length) {
                        const entry = {};
                        headers.forEach((header, index) => {
                            entry[header] = values[index];
                        });
                        if (entry['Site']) {
                            data.push(entry);
                        }
                    }
                }

                resolve(data);
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };

    const onDrop = async (acceptedFiles) => {
        const csvFiles = acceptedFiles.filter(isValidCSV);

        if (csvFiles.length !== acceptedFiles.length) {
            setUploadStatus({ type: 'error', message: 'Invalid file type. Please upload CSV files only.' });
            return;
        }

        setIsUploading(true);
        setUploadStatus(null);

        try {
            const processedData = await Promise.all(csvFiles.map(processCSV));
            const flattenedData = processedData.flat();

            if (flattenedData.length === 0) {
                setUploadStatus({ type: 'warning', message: 'No valid data found in the CSV file.' });
                setIsUploading(false);
                return;
            }

            setProcessedData(flattenedData);
            setFiles(csvFiles);

            const confirmationSummary = flattenedData.reduce((acc, item) => {
                const action = item['Transaction Flag'].toLowerCase();
                if (!acc[action]) acc[action] = 0;
                acc[action]++;
                return acc;
            }, {});

            setConfirmationData(confirmationSummary);
            setUploadStatus({ type: 'info', message: 'Please review the changes and confirm.' });
        } catch (error) {
            console.error('Error processing CSV:', error);
            setUploadStatus({ type: 'error', message: 'Error processing CSV. Please try again.' });
        } finally {
            setIsUploading(false);
        }
    };

    const calculateHash = (data) => {
        // Convert the data array to a JSON string without sorting
        const jsonString = JSON.stringify(data);
        console.log('JSON string to be hashed:', jsonString);
        
        // Calculate SHA-256 hash using CryptoJS
        const hash = crypto.SHA256(jsonString);
        const hashString = hash.toString(crypto.enc.Hex);
        console.log('Calculated hash:', hashString);

        return hashString;
    };

    const handleConfirm = async () => {
        setIsUploading(true);
        setUploadStatus(null);

        try {
            const hash = calculateHash(processedData);
            console.log('Final SHA256 Hash:', hash);

            // Use study ID from URL parameters instead of sponsor code
            const payload = {
                data: processedData,
                hash: hash,
                study_id: studyID
            };

            const response = await fileUploadService.uploadFile(payload);

            console.log('Server resoonse:', response); // logging entire response

            if (response) {
                const processedItems = response.processed_items || [];
                const unprocessedItems = response.unprocessed_items || [];

                // Clear confirmation dialog and parsed data so the form resets
                setConfirmationData(null);
                setProcessedData([]);
                setFiles([]);

                setUploadStatus({
                    type: 'info',
                    message: `Upload complete. ${processedItems.length} items processed, ${unprocessedItems.length} items unprocessed.`,
                    details: {
                        processed_items: processedItems,
                        unprocessed_items: unprocessedItems
                    }
                });

                toast.success(`Upload complete. ${processedItems.length} items processed, ${unprocessedItems.length} items unprocessed.`);
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (error) {
            console.error('Error uploading data:', error);
            setUploadStatus({ type: 'error', message: 'Error uploading data. Please try again.' });
            toast.error('Error uploading data. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };


    const handleCancel = () => {
        setConfirmationData(null);
        setProcessedData([]);
        setFiles([]);
        setUploadStatus(null);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    const removeFile = (index) => {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const downloadSampleCSV = () => {

        // TODO add second button for site csv 
        const adduserheaders = ['Site', 'Last Name', 'First Name', 'Role', 'Phone Number', 'Cell Phone Number', 'Fax Number', 'Email', 'Institution Name', 'Time Zone', 'GMT Offset', 'remarks', 'Transaction Flag'];        
        const usercsv = adduserheaders.join(',') + '\n';

        const addsiteheaders = ['Site', 'Site name in eCOA', 'Site Ready Date', 'Investigator Last Name', 'Investigator First Name', 'Institution Name', 'Address Line 1', 'Address line 2', 'City', 'State', 'Zip', 'Country', 'Phone Number', 'Investigator Cell Phone Number', 'Inv Fax Number', 'Inv Email', 'Study Coordinator Contact', 'SC Phone #', 'SC Email', 'SC Fax #', 'eCOA shipment Contact', 'eCOA shpment email', 'Time Zone', 'GMT Offset', 'Remarks', 'Transaction Flag'];
        const sitecsv = addsiteheaders.join(',') + '\n';
        const blob = new Blob([usercsv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'sample_upload.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };


     return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200 mb-4 inline-block"
                    >
                        <ChevronLeft className="w-5 h-5 mr-1" />
                        Back to Study
                    </button>
                </div>

                {/* Header Section */}
                <div className="max-w-3xl mx-auto text-center mb-16">
                    <h1 className="text-4xl font-medium text-gray-900 mb-4">
                        Data Upload
                    </h1>
                    <p className="text-lg text-gray-600 mb-8">
                        Upload and process your files to create users, study, and site records.
                    </p>
                    <div className="inline-flex p-1 bg-gray-100/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-lg">
                        <button
                            onClick={downloadSampleCSV}
                            className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors rounded-md"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Download Template
                        </button>
                        <button
                            onClick={() => setShowHowToUse(!showHowToUse)}
                            className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors rounded-md"
                        >
                            <HelpCircle className="w-4 h-4 mr-2" />
                            View Guide
                        </button>
                    </div>
                </div>

                {/* Main Upload Section */}
                <div className="max-w-3xl mx-auto">
                    <div className="relative mb-12">
                        <div
                            {...getRootProps()}
                            className={`relative transition-all duration-300 group ${
                                isDragActive
                                    ? 'bg-primary-50/50 border-primary-500 shadow-lg dark:shadow-gray-900/20'
                                    : 'bg-white dark:bg-gray-800 hover:bg-gray-50/80 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-700 hover:border-gray-300'
                            } border-2 border-dashed rounded-2xl backdrop-blur-sm`}
                        >
                            <input {...getInputProps()} />
                            <div className="px-8 py-16 text-center">
                                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-colors duration-300 ${
                                    isDragActive ? 'bg-primary-100' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-gray-200 dark:group-hover:bg-gray-700'
                                }`}>
                                    <Upload className={`h-8 w-8 ${isDragActive ? 'text-primary-600' : 'text-gray-500'}`} />
                                </div>
                                <p className="text-base text-gray-600 mb-2">
                                    {isDragActive
                                        ? "Drop your files here"
                                        : "Drag & drop your CSV files here"}
                                </p>
                                <p className="text-sm text-gray-500">
                                    or <span className="text-primary-600 hover:text-primary-700 cursor-pointer">browse</span> to choose files
                                </p>
                            </div>
                        </div>

                        {/* File List */}
                        {files.length > 0 && (
                            <div className="mt-8">
                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm dark:shadow-gray-900/20 divide-y divide-gray-200 dark:divide-gray-700">
                                    {files.map((file, index) => (
                                        <div 
                                            key={index} 
                                            className="flex items-center justify-between py-4 px-6"
                                        >
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-4">
                                                    <FileText className="h-5 w-5 text-gray-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                                    <p className="text-sm text-gray-500">{Math.round(file.size / 1024)} KB</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeFile(index)}
                                                className="ml-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                            >
                                                <X className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Help Guide */}
                    {showHowToUse && (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm dark:shadow-gray-900/20 mb-12 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                                <h2 className="text-lg font-medium text-gray-900">Upload Guide</h2>
                                <button 
                                    onClick={() => setShowHowToUse(false)}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    <X className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                </button>
                            </div>
                            <div className="p-6 bg-gray-50/50 dark:bg-gray-900/50">
                                <div className="space-y-6">
                                    {[
                                        "Download the CSV template using the button above",
                                        "Fill in your data following the template structure",
                                        "Ensure Protocol format follows: StudyTitle-StudyCode",
                                        "Set appropriate Transaction Flag (Add/Edit/Delete)",
                                        "Upload your completed CSV file",
                                        "Review and confirm the proposed changes"
                                    ].map((step, index) => (
                                        <div key={index} className="flex items-start">
                                            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mr-4">
                                                <span className="text-sm font-medium text-primary-600">{index + 1}</span>
                                            </div>
                                            <p className="text-gray-600 text-sm pt-1.5">{step}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Status Messages */}
                    {uploadStatus && (
                        <div className="mb-12">
                            <div className={`rounded-xl border shadow-sm dark:shadow-gray-900/20 overflow-hidden ${
                                uploadStatus.type === 'success' ? 'border-green-200' :
                                uploadStatus.type === 'error' ? 'border-red-200' :
                                uploadStatus.type === 'info' ? 'border-primary-200' :
                                'border-yellow-200'
                            }`}>
                                <div className={`px-6 py-4 ${
                                    uploadStatus.type === 'success' ? 'bg-green-50' :
                                    uploadStatus.type === 'error' ? 'bg-red-50' :
                                    uploadStatus.type === 'info' ? 'bg-primary-50' :
                                    'bg-yellow-50'
                                }`}>
                                    <div className="flex items-center">
                                        {uploadStatus.type === 'success' && <CheckCircle className="h-5 w-5 text-green-500 mr-3" />}
                                        {uploadStatus.type === 'error' && <AlertCircle className="h-5 w-5 text-red-500 mr-3" />}
                                        {uploadStatus.type === 'info' && <AlertCircle className="h-5 w-5 text-primary-500 mr-3" />}
                                        {uploadStatus.type === 'warning' && <AlertCircle className="h-5 w-5 text-yellow-500 mr-3" />}
                                        <span className="text-sm font-medium">{uploadStatus.message}</span>
                                    </div>
                                </div>
                                {uploadStatus.details && (
                                    <div className="bg-white dark:bg-gray-800 px-6 py-4">
                                        <div className="space-y-4">
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-900 mb-3">Processed Items</h4>
                                                <div className="space-y-2">
                                                    {uploadStatus.details.processed_items.map((item, index) => (
                                                        <div key={index} className="flex items-center text-sm text-gray-600">
                                                            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                                                            {`${item.action}: ${item.site_code || item.email}`}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            {uploadStatus.details.unprocessed_items.length > 0 && (
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900 mb-3">Failed Items</h4>
                                                    <div className="space-y-3">
                                                        {uploadStatus.details.unprocessed_items.map((item, index) => (
                                                            <div key={index} className="bg-red-50 rounded-lg p-3">
                                                                <div className="text-sm text-gray-900">
                                                                    {`${item['Transaction Flag']}: ${item['Site'] || item['Inv Email']}`}
                                                                </div>
                                                                <div className="text-sm text-red-600 mt-1">
                                                                    {item.error}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Confirmation Dialog */}
                    {confirmationData && (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm dark:shadow-gray-900/20 overflow-hidden mb-12">
                            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-medium text-gray-900">Review Changes</h3>
                            </div>
                            <div className="px-6 py-4">
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr className="bg-gray-50 dark:bg-gray-900">
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Action
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Count
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {Object.entries(confirmationData).map(([action, count], index) => (
                                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                    <td className="px-6 py-4 text-sm text-gray-900 capitalize">
                                                        {action}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800">
                                                            {count}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                                <button
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                                >
                                    Confirm Changes
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Processed Data */}
                    {processedData.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm dark:shadow-gray-900/20 overflow-hidden">
                            <button
                                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                onClick={() => setShowProcessedData(!showProcessedData)}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="font-medium text-gray-900">Processed Data</span>
                                    <span className="text-sm text-gray-500">({processedData.length} items)</span>
                                </div>
                                {showProcessedData ? (
                                    <ChevronUp className="h-5 w-5 text-gray-400" />
                                ) : (
                                    <ChevronDown className="h-5 w-5 text-gray-400" />
                                )}
                            </button>
                            {showProcessedData && (
                                <div className="border-t border-gray-200 dark:border-gray-700">
                                    <div className="p-4 space-y-4">
                                        {/* Group items by transaction type */}
                                        {(() => {
                                            const grouped = processedData.reduce((acc, item) => {
                                                const flag = item['Transaction Flag']?.toLowerCase() || 'unknown';
                                                if (!acc[flag]) acc[flag] = [];
                                                acc[flag].push(item);
                                                return acc;
                                            }, {});

                                            const actionConfig = {
                                                'add site': { label: 'Add Sites', color: 'blue', icon: '🏢' },
                                                'add user': { label: 'Add Users', color: 'green', icon: '👤' },
                                                'edit site': { label: 'Edit Sites', color: 'yellow', icon: '✏️' },
                                                'delete user': { label: 'Delete Users', color: 'red', icon: '🗑️' },
                                            };

                                            return Object.entries(grouped).map(([flag, items]) => {
                                                const config = actionConfig[flag] || { label: flag, color: 'gray', icon: '📋' };
                                                const isSiteAction = flag.includes('site');
                                                
                                                return (
                                                    <div key={flag} className="bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden">
                                                        <div className={`px-4 py-2 border-l-4 ${
                                                            config.color === 'blue' ? 'border-blue-500 bg-blue-50' :
                                                            config.color === 'green' ? 'border-green-500 bg-green-50' :
                                                            config.color === 'yellow' ? 'border-yellow-500 bg-yellow-50' :
                                                            config.color === 'red' ? 'border-red-500 bg-red-50' :
                                                            'border-gray-500 bg-gray-50 dark:bg-gray-900'
                                                        }`}>
                                                            <span className="text-sm font-medium text-gray-900">
                                                                {config.icon} {config.label} ({items.length})
                                                            </span>
                                                        </div>
                                                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                                            {items.map((item, idx) => (
                                                                <div key={idx} className="px-4 py-3 bg-white dark:bg-gray-800 flex items-center gap-4">
                                                                    <div className="flex-shrink-0">
                                                                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium ${
                                                                            config.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                                                                            config.color === 'green' ? 'bg-green-100 text-green-700' :
                                                                            config.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                                                                            config.color === 'red' ? 'bg-red-100 text-red-700' :
                                                                            'bg-gray-100 dark:bg-gray-700 text-gray-700'
                                                                        }`}>
                                                                            {idx + 1}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                                                                        <div>
                                                                            <span className="text-gray-500">Site: </span>
                                                                            <span className="font-medium text-gray-900">{item.Site || '-'}</span>
                                                                        </div>
                                                                        {isSiteAction ? (
                                                                            <>
                                                                                <div>
                                                                                    <span className="text-gray-500">Institution: </span>
                                                                                    <span className="text-gray-900">{item['Institution Name'] || '-'}</span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="text-gray-500">Timezone: </span>
                                                                                    <span className="text-gray-900">{item['Time Zone'] || '-'}</span>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <div>
                                                                                    <span className="text-gray-500">Name: </span>
                                                                                    <span className="text-gray-900">
                                                                                        {item['First Name'] || item['Last Name'] 
                                                                                            ? `${item['First Name'] || ''} ${item['Last Name'] || ''}`.trim()
                                                                                            : '-'
                                                                                        }
                                                                                    </span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="text-gray-500">Role: </span>
                                                                                    <span className="text-gray-900">{item.Role || '-'}</span>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    {!isSiteAction && item.Email && (
                                                                        <div className="hidden sm:block flex-shrink-0 text-sm text-gray-500 truncate max-w-[200px]">
                                                                            {item.Email}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Loading Overlay */}
            {isUploading && (
                <div className="fixed inset-0 backdrop-blur-sm bg-black/30 dark:bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20 px-8 py-6 max-w-sm mx-auto">
                        <div className="flex items-center justify-center space-x-4">
                            <div className="relative">
                                <div className="w-12 h-12 rounded-full border-2 border-primary-600/20 animate-pulse"></div>
                                <Loader className="w-6 h-6 text-primary-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-spin" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-gray-900 font-medium">Processing Files</span>
                                <span className="text-sm text-gray-500">This may take a moment...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUpload;